import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { ShiftTemplate, CreateShiftTemplateData, RoleRequirement } from '../services/api';

interface ShiftTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateShiftTemplateData) => Promise<void>;
  template?: ShiftTemplate | null;
  sections: Array<{ id: string; name: string; color?: string }>;
}

// Predefined colors for shift templates
const TEMPLATE_COLORS = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#6366F1', label: 'Indigo' },
];

// Role options
const ROLES = [
  { value: 'server', label: 'Server' },
  { value: 'bartender', label: 'Bartender' },
  { value: 'host', label: 'Host' },
  { value: 'cook', label: 'Cook' },
  { value: 'manager', label: 'Manager' },
  { value: 'busser', label: 'Busser' },
  { value: 'dishwasher', label: 'Dishwasher' },
];

// Day options (0=Sunday, 1=Monday, etc.)
const DAYS = [
  { value: 1, label: 'M', fullLabel: 'Monday' },
  { value: 2, label: 'T', fullLabel: 'Tuesday' },
  { value: 3, label: 'W', fullLabel: 'Wednesday' },
  { value: 4, label: 'T', fullLabel: 'Thursday' },
  { value: 5, label: 'F', fullLabel: 'Friday' },
  { value: 6, label: 'S', fullLabel: 'Saturday' },
  { value: 0, label: 'S', fullLabel: 'Sunday' },
];

export default function ShiftTemplateModal({
  isOpen,
  onClose,
  onSave,
  template,
  sections,
}: ShiftTemplateModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [repeatDays, setRepeatDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [roleRequirements, setRoleRequirements] = useState<RoleRequirement[]>([]);
  const [activeSectionIds, setActiveSectionIds] = useState<string[]>([]);

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setColor(template.color || '#3B82F6');
      setStartTime(template.startTime);
      setEndTime(template.endTime);
      setRepeatDays(template.repeatDays || []);
      setRoleRequirements(template.roleRequirements || []);
      setActiveSectionIds(template.activeSectionIds || []);
    } else {
      // Reset form for new template
      setName('');
      setDescription('');
      setColor('#3B82F6');
      setStartTime('09:00');
      setEndTime('17:00');
      setRepeatDays([1, 2, 3, 4, 5]);
      setRoleRequirements([]);
      setActiveSectionIds([]);
    }
  }, [template, isOpen]);

  // Toggle day selection
  const toggleDay = (day: number) => {
    setRepeatDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Toggle section selection
  const toggleSection = (sectionId: string) => {
    setActiveSectionIds((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Add role requirement
  const addRoleRequirement = () => {
    setRoleRequirements((prev) => [
      ...prev,
      { role: 'server', minCount: 1, maxCount: undefined },
    ]);
  };

  // Update role requirement
  const updateRoleRequirement = (
    index: number,
    field: keyof RoleRequirement,
    value: string | number | undefined
  ) => {
    setRoleRequirements((prev) =>
      prev.map((req, i) => (i === index ? { ...req, [field]: value } : req))
    );
  };

  // Remove role requirement
  const removeRoleRequirement = (index: number) => {
    setRoleRequirements((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        color,
        startTime,
        endTime,
        repeatDays,
        roleRequirements,
        activeSectionIds,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {template ? 'Edit Shift Template' : 'Create Shift Template'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Lunch Service"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={clsx(
                    'w-8 h-8 rounded-full border-2 transition-all',
                    color === c.value
                      ? 'border-slate-900 scale-110'
                      : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Start Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                End Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                required
              />
            </div>
          </div>

          {/* Repeat Days */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Repeat On
            </label>
            <div className="flex gap-2">
              {DAYS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={clsx(
                    'w-10 h-10 rounded-lg font-medium text-sm transition-colors',
                    repeatDays.includes(day.value)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                  title={day.fullLabel}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Role Requirements */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">
                Staffing Requirements
              </label>
              <button
                type="button"
                onClick={addRoleRequirement}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add Role
              </button>
            </div>

            {roleRequirements.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No staffing requirements defined
              </p>
            ) : (
              <div className="space-y-2">
                {roleRequirements.map((req, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                  >
                    <select
                      value={req.role}
                      onChange={(e) =>
                        updateRoleRequirement(index, 'role', e.target.value)
                      }
                      className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    >
                      {ROLES.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-500">Min:</span>
                      <input
                        type="number"
                        min="0"
                        value={req.minCount}
                        onChange={(e) =>
                          updateRoleRequirement(
                            index,
                            'minCount',
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="w-14 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-500">Max:</span>
                      <input
                        type="number"
                        min="0"
                        value={req.maxCount ?? ''}
                        onChange={(e) =>
                          updateRoleRequirement(
                            index,
                            'maxCount',
                            e.target.value ? parseInt(e.target.value) : undefined
                          )
                        }
                        placeholder="-"
                        className="w-14 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRoleRequirement(index)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Sections */}
          {sections.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sections Open
              </label>
              <div className="flex flex-wrap gap-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      activeSectionIds.includes(section.id)
                        ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                        : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                    )}
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {template ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
